import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import Can from '../../../components/Can';
import SearchableSelect from '../../../components/SearchableSelect';
import UbicacionSelector from '../../../components/UbicacionSelector';
import { CONTRATO_PERIODICIDADES } from '../../../services/contratoVentaService';
import { formatCurrency } from '../../../utils/formatters';

export interface ContratoPOSPanelForm {
  idCompania: string;
  nroSucursalVenta: number;
  sucursalNombre: string;
  nroUbicacionOrigen: number;
  ubicacionLabel: string;
  codigoMoneda: number;
  monedaDescripcion: string;
  fechaInicio: string;
  fechaFin: string;
  periodicidad: string;
  modoFacturacion: string;
  diaFacturacion: string;
  codigoLista: string;
  tipoContratoId: number | '';
  codigoContrato: string;
  nroOrdenCompraCliente: string;
  observaciones: string;
  estado: string;
}

interface CompaniaOption {
  idCompania: string;
  nombre: string;
}

interface TipoContratoOption {
  id: number;
  nombre: string;
  activo: boolean;
}

interface Props {
  form: ContratoPOSPanelForm;
  onChange: (patch: Partial<ContratoPOSPanelForm>) => void;
  companias: CompaniaOption[];
  tiposContrato: TipoContratoOption[];
  readOnly?: boolean;
  isNew: boolean;
  saving: boolean;
  cantItems: number;
  totalEstimado: number;
  onOpenMoneda: () => void;
  onOpenSucursal: () => void;
  onOpenLista: () => void;
  onOpenTipos: () => void;
  onTrackObsCaret?: (start: number, end: number) => void;
  onGuardar: (activar?: boolean) => void;
  onActivar?: () => void;
  onSuspender?: () => void;
  onReanudar?: () => void;
  onAnular?: () => void;
}

const ContratoPOSPanel: React.FC<Props> = ({
  form,
  onChange,
  companias,
  tiposContrato,
  readOnly = false,
  isNew,
  saving,
  cantItems,
  totalEstimado,
  onOpenMoneda,
  onOpenSucursal,
  onOpenLista,
  onOpenTipos,
  onTrackObsCaret,
  onGuardar,
  onActivar,
  onSuspender,
  onReanudar,
  onAnular,
}) => {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        p: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="overline" color="text.secondary">
            Contrato
          </Typography>
          {!isNew && (
            <Chip size="small" label={form.estado} color={form.estado === 'Vigente' ? 'success' : 'default'} />
          )}
        </Stack>

        <SearchableSelect
          label="Compañía"
          required
          fullWidth
          size="small"
          disabled={readOnly}
          allowEmpty
          emptyOptionLabel="Seleccione compañía"
          value={form.idCompania || null}
          onChange={(v) => onChange({ idCompania: String(v ?? '').trim() })}
          options={companias.map((c) => ({
            value: c.idCompania,
            label: c.nombre ? `${c.idCompania} - ${c.nombre}` : c.idCompania,
          }))}
        />

        <TextField
          label="Sucursal venta"
          fullWidth
          size="small"
          required
          value={form.sucursalNombre || (form.nroSucursalVenta ? String(form.nroSucursalVenta) : '')}
          InputProps={{
            readOnly: true,
            endAdornment: !readOnly ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onOpenSucursal}>
                  <SearchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          onClick={!readOnly ? onOpenSucursal : undefined}
        />

        <UbicacionSelector
          label="Ubicación origen"
          value={form.ubicacionLabel}
          disabled={readOnly}
          autoSelectPreferida={isNew}
          onChange={(label, nro) =>
            onChange({
              ubicacionLabel: label,
              nroUbicacionOrigen: nro && nro > 0 ? nro : 0,
            })
          }
        />

        <TextField
          label="Moneda"
          fullWidth
          size="small"
          value={form.monedaDescripcion || form.codigoMoneda}
          InputProps={{
            readOnly: true,
            endAdornment: !readOnly ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onOpenMoneda}>
                  <SearchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          onClick={!readOnly ? onOpenMoneda : undefined}
        />

        <Stack direction="row" spacing={1}>
          <TextField
            type="date"
            label="Inicio"
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            value={form.fechaInicio}
            onChange={(e) => onChange({ fechaInicio: e.target.value })}
            disabled={readOnly}
          />
          <TextField
            type="date"
            label="Fin"
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            value={form.fechaFin}
            onChange={(e) => onChange({ fechaFin: e.target.value })}
            disabled={readOnly}
          />
        </Stack>

        <TextField
          select
          label="Periodicidad"
          fullWidth
          size="small"
          value={form.periodicidad}
          onChange={(e) => onChange({ periodicidad: e.target.value })}
          disabled={readOnly}
        >
          {CONTRATO_PERIODICIDADES.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Modo facturación"
          fullWidth
          size="small"
          value={form.modoFacturacion}
          onChange={(e) => onChange({ modoFacturacion: e.target.value })}
          disabled={readOnly}
        >
          <MenuItem value="InicioPeriodo">Al inicio del período</MenuItem>
          <MenuItem value="FinPeriodo">Al fin del período</MenuItem>
        </TextField>

        <TextField
          label="Día facturación"
          fullWidth
          size="small"
          type="number"
          value={form.diaFacturacion}
          onChange={(e) => onChange({ diaFacturacion: e.target.value })}
          disabled={readOnly}
          inputProps={{ min: 1, max: 31 }}
        />

        <TextField
          label="Lista de precios"
          fullWidth
          size="small"
          value={form.codigoLista}
          InputProps={{
            readOnly: true,
            endAdornment: !readOnly ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onOpenLista}>
                  <SearchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          onClick={!readOnly ? onOpenLista : undefined}
          helperText="Opcional; para ítems con modo Lista"
        />

        <Stack direction="row" spacing={0.5} alignItems="flex-start">
          <TextField
            select
            label="Tipo de contrato"
            fullWidth
            size="small"
            value={form.tipoContratoId}
            onChange={(e) =>
              onChange({
                tipoContratoId: e.target.value ? Number(e.target.value) : '',
              })
            }
            disabled={readOnly}
          >
            <MenuItem value="">Sin tipo</MenuItem>
            {tiposContrato
              .filter((t) => t.activo || t.id === form.tipoContratoId)
              .map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.activo ? t.nombre : `${t.nombre} (inactivo)`}
                </MenuItem>
              ))}
          </TextField>
          {!readOnly && (
            <IconButton
              size="small"
              title="Configurar tipos"
              aria-label="Configurar tipos de contrato"
              onClick={onOpenTipos}
              sx={{ mt: 0.5 }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        <TextField
          label="Código de contrato"
          fullWidth
          size="small"
          value={form.codigoContrato}
          onChange={(e) => onChange({ codigoContrato: e.target.value })}
          disabled={readOnly}
          inputProps={{ maxLength: 80 }}
        />

        <TextField
          label="OC cliente"
          fullWidth
          size="small"
          value={form.nroOrdenCompraCliente}
          onChange={(e) => onChange({ nroOrdenCompraCliente: e.target.value })}
          disabled={readOnly}
        />

        <TextField
          label="Observaciones"
          fullWidth
          size="small"
          multiline
          minRows={2}
          value={form.observaciones}
          onChange={(e) => onChange({ observaciones: e.target.value })}
          onSelect={(e) => {
            const t = e.target as HTMLInputElement | HTMLTextAreaElement;
            onTrackObsCaret?.(t.selectionStart ?? 0, t.selectionEnd ?? 0);
          }}
          onBlur={(e) => {
            onTrackObsCaret?.(e.target.selectionStart ?? 0, e.target.selectionEnd ?? 0);
          }}
          disabled={readOnly}
          inputProps={{ 'data-contrato-field': 'observaciones' }}
        />

        <Divider />

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {cantItems} producto{cantItems === 1 ? '' : 's'}
          </Typography>
          <Typography variant="body2" fontWeight={700} color="primary.main">
            Est. {formatCurrency(totalEstimado)}
          </Typography>
        </Stack>

        {!readOnly && (
          <Can permiso={isNew ? 'ventas.contratos.nuevo' : 'ventas.contratos.editar'}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<SaveIcon />}
              disabled={saving}
              onClick={() => onGuardar(false)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </Can>
        )}

        {isNew && (
          <Can permiso="ventas.contratos.nuevo">
            <Button
              variant="contained"
              color="success"
              fullWidth
              disabled={saving}
              onClick={() => onGuardar(true)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Guardar y activar
            </Button>
          </Can>
        )}

        {!isNew && form.estado === 'Borrador' && onActivar && (
          <Can permiso="ventas.contratos.editar">
            <Button variant="outlined" fullWidth onClick={onActivar} sx={{ textTransform: 'none' }}>
              Activar
            </Button>
          </Can>
        )}
        {!isNew && form.estado === 'Vigente' && onSuspender && (
          <Can permiso="ventas.contratos.editar">
            <Button
              variant="outlined"
              color="warning"
              fullWidth
              onClick={onSuspender}
              sx={{ textTransform: 'none' }}
            >
              Suspender
            </Button>
          </Can>
        )}
        {!isNew && form.estado === 'Suspendido' && onReanudar && (
          <Can permiso="ventas.contratos.editar">
            <Button variant="outlined" fullWidth onClick={onReanudar} sx={{ textTransform: 'none' }}>
              Reanudar
            </Button>
          </Can>
        )}
        {!isNew && form.estado !== 'Anulado' && form.estado !== 'Finalizado' && onAnular && (
          <Can permiso="ventas.contratos.anular">
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={onAnular}
              sx={{ textTransform: 'none' }}
            >
              Anular
            </Button>
          </Can>
        )}
      </Stack>
    </Box>
  );
};

export default ContratoPOSPanel;
